import { gameState } from "./state";
import { landscape, level, nextZone, getZoneNameTid } from "./terrain";
import { copter } from "./copter";
import { background } from "./background";
import { shaker } from "./shaker";
import { curtains } from "./curtains";
import { camera } from "./camera";

import { hud } from "./hud";
import { spawnTurret, updateTurrets, resetTurrets } from "./turret";
import { updateBullets, resetBullets, resetBirds, resetCoins, resetPickups } from "./entities";
import { spawnCoin, updateCoins, spawnBirds, updateBirds, spawnPickup, updatePickups } from "./spawn";
import { bonusLayout } from "./bonus-layout";
import { updateEffects } from "./effects";
import { input } from "shared";
import { playMusic, stopMusic, playSound } from "audio";
import { Asset } from "assets.gen";
import { t, Tid } from "i18n";
import { BONUS_LAYOUT_INTERVAL, ACCEL_AMOUNT, ACCEL_LIMIT, GAME_W, GAME_H, PICKUP_INTERVAL, FPS } from "./data";
import { dt } from "lib/loop";

export function update(_dt: number): void {
  if (gameState.pauseMode) {
    if (input.actionPressed) {
      gameState.pauseMode = false;
    }
    return;
  }

  if (gameState.showGameOver) {
    if (!gameOverScreen.visible) {
      gameOverScreen.show();
    }
    if (input.actionPressed) {
      gameState.showGameOver = false;
      curtains.close(1, () => {
        gameOverScreen.visible = false;
        gameState.emulation = true;
        menu.visible = true;
        gameState.zoneIndex = 0;
        resetGame(true);
      });
    }
    curtains.update();
    return;
  }

  if (gameState.waitingToStart) {
    if (input.actionPressed) {
      gameState.waitingToStart = false;
      gameState.started = true;
      playMusic();
    }
  } else if (gameState.started || gameState.emulation) {
    gameState.tick += dt;

    if (!gameState.showGameOver && !levelCompleteScreenObj.visible) {
      if (level.coins) {
        if (gameState.tick >= gameState.nextCoin) {
          gameState.nextCoin += level.coinInterval / FPS;
          spawnCoin();
        }
        if (gameState.tick >= gameState.nextBonus) {
          gameState.nextBonus += BONUS_LAYOUT_INTERVAL / FPS;
          bonusLayout.replace();
        }
      }

      if (level.turrets) {
        if (gameState.tick >= gameState.nextTurret) {
          gameState.nextTurret += level.turretInterval / FPS;
          spawnTurret();
        }
      }

      if (level.birdInterval) {
        if (gameState.tick >= gameState.nextBird) {
          gameState.nextBird += level.birdInterval / FPS;
          spawnBirds();
        }
      }

      if (gameState.tick >= gameState.nextPickup) {
        gameState.nextPickup += PICKUP_INTERVAL / FPS;
        spawnPickup();
      }
    }

    if (!gameState.emulation) {
      if (copter.wx >= gameState.accelDist && landscape.vx < ACCEL_LIMIT) {
        landscape.vx += ACCEL_AMOUNT * level.speedMultiplier;
        gameState.accelDist += level.zoneDistance * gameState.accelTime;
        playSound(Asset.sfx_speed_up);
      }

      gameState.dist = copter.wx | 0;

      if (gameState.dist > level.zoneDistance) {
        completeLevel();
      }
    }
  } else if (input.actionPressed && gameState.active) {
    gameState.started = true;
    playMusic();
  }

  shaker.update();
  startScreen.update();
  landscape.update();

  if (gameState.emulation) {
    if (copter._pilot) {
      copter._pilot.update(copter.centre);
    }
    copter.update();
    camera.update(copter.wx);
    landscape.syncFrame(camera.x);

    updateTurrets();
    updateBullets();
    updateBirds();
    updateCoins();
    updatePickups();
    updateEffects();
  } else if (!gameState.showGameOver && !gameState.waitingToStart && !levelCompleteScreenObj.visible) {
    if (copter._pilot) {
      copter._pilot.update(copter.centre);
    }
    copter.update();
    camera.update(copter.wx);
    landscape.syncFrame(camera.x);

    updateTurrets();
    updateBullets();
    updateBirds();
    updateCoins();
    updatePickups();
    updateEffects();
  } else {
    camera.update(copter.wx);
    landscape.syncFrame(camera.x);
  }
  background.update();
  curtains.update();

  menu.update();
  levelCompleteScreenObj.update();
  zoneBanner.update();
}

function completeLevel(): void {
  gameState.started = false;
  gameState.active = false;
  gameState.emulation = true;
  hud.reset();
  copter.changePilot();
  copter.godMode(-1);
  copter.visible = true;
  stopMusic();
  resetTurrets();
  resetBullets();
  resetBirds();
  resetCoins();
  resetPickups();
  levelCompleteScreenObj.show();
}

export function resetGame(advanceZone: boolean = true): void {
  gameState.active = true;
  if (!gameState.emulation) {
    gameState.dist = 0;
    gameState.score = 0;
  }

  if (advanceZone) nextZone();
  hud.reset();
  background.reset();
  landscape.reset();
  copter.reset();
  camera.reset(copter.wx);
  landscape.syncFrame(camera.x);
  resetTurrets();
  resetBullets();
  resetBirds();
  resetCoins();
  resetPickups();
  bonusLayout.firstPlace();
  if (!gameState.emulation) zoneBanner.show();

  gameState.tick = 0;
  gameState.accelDist = 200;
  gameState.accelTime = 0.06;
  gameState.nextCoin = level.coinInterval / FPS;
  gameState.nextBonus = BONUS_LAYOUT_INTERVAL / FPS;
  gameState.nextTurret = level.turretInterval / FPS;
  gameState.nextBird = level.birdInterval / FPS;
  gameState.nextPickup = PICKUP_INTERVAL / FPS;
}

export const zoneBanner = {
  visible: false,
  _timer: 0,
  _duration: 3,

  show(): void {
    this.visible = true;
    this._timer = this._duration;
  },

  update(): void {
    if (!this.visible) return;
    this._timer -= dt;
    if (this._timer <= 0) this.visible = false;
  },

  render(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D): void {
    if (!this.visible) return;
    const progress = this._timer / this._duration;
    let alpha: number;
    if (progress > 0.8) alpha = (1 - progress) / 0.2;
    else if (progress < 0.2) alpha = progress / 0.2;
    else alpha = 1;

    ctx.save();
    ctx.globalAlpha = alpha * 0.7;
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, (GAME_H * 0.15) | 0, GAME_W, 20);
    ctx.globalAlpha = alpha;
    ctx.font = '8px "press-start"';
    ctx.fillStyle = "#ffffff";
    ctx.textBaseline = "middle";
    const zoneTid = getZoneNameTid();
    const text = t(zoneTid);
    const metrics = ctx.measureText(text);
    ctx.fillText(text, ((GAME_W - metrics.width) * 0.5) | 0, (GAME_H * 0.15 + 10) | 0);
    ctx.restore();
  },
};

export const startScreen = {
  _blinkT: 0,

  update(): void {
    this._blinkT += dt;
  },

  render(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D): void {
    if (!gameState.waitingToStart) return;

    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fillRect(0, 0, GAME_W, GAME_H);

    ctx.font = '8px "press-start"';
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";

    const tutorialHold = t(Tid.tutorial_hold);
    const tutorialRelease = t(Tid.tutorial_release);

    let alpha = 1;
    if (((this._blinkT * 30) | 0) % 20 < 10) {
      alpha = 0.5;
    }
    ctx.globalAlpha = alpha;
    ctx.fillStyle = "#ffffff";
    ctx.fillText(t(Tid.any_key_to_start), GAME_W * 0.5, GAME_H * 0.35);
    ctx.globalAlpha = 1;

    ctx.fillStyle = "#aaffaa";
    ctx.fillText(tutorialHold, GAME_W * 0.5, GAME_H * 0.5);
    ctx.fillText(tutorialRelease, GAME_W * 0.5, GAME_H * 0.6);

    ctx.textAlign = "left";
  },
};

export const menu = {
  _blinkT: 0,
  visible: true,

  reset(): void {
    this.visible = true;
    gameState.emulation = true;
  },

  update(): void {
    if (!this.visible) return;
    this._blinkT += dt;
    if (input.actionPressed && gameState.emulation) {
      gameState.emulation = false;
      curtains.close(1, () => {
        menu.visible = false;
        resetGame(false);
        gameState.waitingToStart = true;
      });
    }
  },

  render(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D): void {
    if (!this.visible) return;
    ctx.fillStyle = "rgba(0,0,0,0.2)";
    ctx.fillRect(0, 0, GAME_W, GAME_H);

    if (((this._blinkT * 30) | 0) % 20 < 10) {
      ctx.font = '8px "press-start"';
      ctx.fillStyle = "#ffffff";
      ctx.textBaseline = "top";
      ctx.textAlign = "center";
      ctx.fillText(t(Tid.any_key_to_start), GAME_W >> 1, GAME_H - 14);
    }
  },
};

export const levelCompleteScreenObj = {
  visible: false,
  _active: false,

  show(): void {
    this.visible = true;
    this._active = false;
    setTimeout(() => {
      this._active = true;
    }, 2000);
  },

  update(): void {
    if (!this.visible) return;
    if (this._active && input.actionPressed) {
      gameState.emulation = false;
      curtains.close(1, () => {
        this.visible = false;
        gameState.active = true;
        gameState.dist = 0;
        gameState.score = 0;
        resetGame();
        gameState.waitingToStart = true;
      });
    }
  },

  render(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D): void {
    if (!this.visible) return;
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fillRect(0, 0, GAME_W, GAME_H);
    ctx.font = '8px "press-start"';
    ctx.fillStyle = "#ffffff";
    ctx.textBaseline = "middle";
    const text = t(Tid.level_complete);
    const metrics = ctx.measureText(text);
    ctx.fillText(text, ((GAME_W - metrics.width) * 0.5) | 0, (GAME_H * 0.5) | 0);
  },
};

export const gameOverScreen = {
  visible: false,

  show(): void {
    this.visible = true;
  },

  hide(): void {
    this.visible = false;
  },

  render(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D): void {
    if (!this.visible) return;
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(0, 0, GAME_W, GAME_H);

    ctx.font = '8px "press-start"';
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#ff4444";
    const overText = t(Tid.game_over);
    const overMetrics = ctx.measureText(overText);
    ctx.fillText(overText, ((GAME_W - overMetrics.width) * 0.5) | 0, (GAME_H * 0.4) | 0);

    ctx.fillStyle = "#ffffff";
    const scoreText = `${t(Tid.score_label)}: ${gameState.score}`;
    const scoreMetrics = ctx.measureText(scoreText);
    ctx.fillText(scoreText, ((GAME_W - scoreMetrics.width) * 0.5) | 0, (GAME_H * 0.55) | 0);
  },
};
